import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import type { AnyRouter } from "@tanstack/react-router";
import { queryClient } from "../libs/react-query";
import { registerLogoutHandler } from "../libs/axios";
import { LOGIN_PATH, clearProfile } from "../auth";
import { Toaster } from "../toast";
import { ErrorBoundary } from "../components";
import { PreferredLanguageSync } from "./PreferredLanguageSync";

/**
 * App shell / provider composition. Wires the seams left open across 6/7 and mounts the
 * provider tree.
 *
 * Seam wiring (once, on mount):
 *  - The axios request/response interceptors are already installed at instance creation (6b)
 *    and read the access token from the Zustand auth store via the auth-seam (6e) — nothing
 *    to re-install here.
 *  - We register the app-level logout handler the interceptor calls on refresh failure:
 *    signalLogout() clears the store, then this handler performs the navigation.
 *
 * Provider order (outer → inner): QueryClientProvider → ErrorBoundary → RouterProvider,
 * with <Toaster/> a sibling of the boundary so toasts survive a route-subtree crash.
 * No provider is added for useUIStore (unvalidated — no consumer yet).
 */
interface AppProviderProps {
  /**
   * The app router, INJECTED by the entry point — core must never import `src/router.ts`
   * itself. That module pulls `routeTree.gen`, which imports every route, which imports
   * `@modules/*`; importing it here would make core transitively depend on a feature module
   * (architecture.md: "@core may not import from any module"). Taking it as a prop keeps the
   * arrow pointing the right way — entry → core, never core → routes. Typed as `AnyRouter`
   * so core stays ignorant of the concrete route tree. Enforced by an ESLint zone.
   */
  router: AnyRouter;
}

export function AppProvider({ router }: AppProviderProps) {
  useEffect(() => {
    registerLogoutHandler(() => {
      // Session lost (the interceptor's refresh failed). signalLogout already cleared the
      // in-memory token; drop the cached profile too so guards don't immediately bounce the
      // user back in, then route to login.
      clearProfile(queryClient);
      // Skip navigating when we're already at login (e.g. a failed /me probe during startup
      // rehydration) — the interceptor fires this on every refresh failure, and re-navigating
      // to the current route would loop the probe.
      if (router.state.location.pathname !== LOGIN_PATH) {
        void router.navigate({ to: LOGIN_PATH });
      }
    });
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Inside the QueryClientProvider (it observes the /me cache), outside the boundary so a
          route crash can't strand the app in the wrong language/direction. */}
      <PreferredLanguageSync />
      <ErrorBoundary label="app">
        <RouterProvider router={router} />
      </ErrorBoundary>
      <Toaster />
    </QueryClientProvider>
  );
}
